import axios from "axios";

import { emitLogout, onLogout } from "./authEvents";
import { API_BASE, AUTH_ENDPOINTS } from "./endpoints";
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "./tokenStore";

const apiClient = axios.create({
  baseURL: API_BASE,
});

const refreshClient = axios.create({
  baseURL: API_BASE,
});

let isRefreshing = false;
let refreshQueue = [];
let authEventsBound = false;

function setAuthorizationHeader(config, token) {
  config.headers = config.headers || {};

  if (typeof config.headers.set === "function") {
    config.headers.set("Authorization", `Bearer ${token}`);
    return;
  }

  config.headers.Authorization = `Bearer ${token}`;
}

function queueRequest() {
  return new Promise((resolve, reject) => {
    refreshQueue.push({ resolve, reject });
  });
}

function flushQueue(error, accessToken = null) {
  refreshQueue.forEach((pending) => {
    if (error) {
      pending.reject(error);
      return;
    }
    pending.resolve(accessToken);
  });
  refreshQueue = [];
}

function isRefreshRequest(config) {
  const url = String(config?.url || "");
  return url.includes(AUTH_ENDPOINTS.refresh);
}

async function requestTokenRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error("Refresh token not available.");
  }

  const response = await refreshClient.post(
    AUTH_ENDPOINTS.refresh,
    { refresh: refreshToken },
    {
      skipAuth: true,
      skipRefresh: true,
    }
  );

  const access = response?.data?.access;
  const rotatedRefresh = response?.data?.refresh || refreshToken;

  if (!access) {
    throw new Error("Refresh response did not include an access token.");
  }

  setTokens({
    access,
    refresh: rotatedRefresh,
  });

  return access;
}

function bindAuthEvents() {
  if (authEventsBound) {
    return;
  }
  authEventsBound = true;

  onLogout(() => {
    clearTokens();
  });
}

bindAuthEvents();

apiClient.interceptors.request.use(
  (config) => {
    if (config?.skipAuth) {
      return config;
    }

    const accessToken = getAccessToken();
    if (accessToken) {
      setAuthorizationHeader(config, accessToken);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    if (!originalRequest || status !== 401 || originalRequest?.skipRefresh) {
      return Promise.reject(error);
    }

    if (originalRequest._retry || isRefreshRequest(originalRequest)) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearTokens();
      emitLogout({ reason: "missing_refresh" });
      return Promise.reject(error);
    }

    if (isRefreshing) {
      try {
        const newAccessToken = await queueRequest();
        setAuthorizationHeader(originalRequest, newAccessToken);
        return apiClient(originalRequest);
      } catch (queueError) {
        return Promise.reject(queueError);
      }
    }

    isRefreshing = true;

    try {
      const newAccessToken = await requestTokenRefresh();
      flushQueue(null, newAccessToken);
      setAuthorizationHeader(originalRequest, newAccessToken);
      return apiClient(originalRequest);
    } catch {
      flushQueue(error, null);
      clearTokens();
      emitLogout({ reason: "refresh_failed" });
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
